<template>
  <b-tab :title="$t('Accounting')">
    <div class="accounting-tab">
      <submit-button v-if="!isConnected" :show-loader="loading" @click="onConnectClick" class="mb-2">
        {{ $t("Connect to Accounting System") }}
      </submit-button>
      <div>
        <spinner :show="loading" height="5"/>
        <div v-if="isConnected && !loading">
          <div v-if="tenants">
            <div class="card mt-2">
              <div class="card-header">
                <h5 class="d-inline-block mb-0">
                  {{ $t('Please, choose tenant') }}
                </h5>
              </div>
              <div class="card-body">
                <b-form-select v-model="selectedTenantId" :options="tenants"></b-form-select>
              </div>
              <div class="card-footer">
                <submit-button :disabled="!selectedTenantId" :show-loader="savingTenant" @click="chooseTenant">
                  {{ $t('Submit') }}
                </submit-button>
              </div>
            </div>
          </div>
          <div v-else class="grid-wrapper">
            <div class="card">
              <div class="card-header">
                <h5 class="d-inline-block mb-0">
                  {{ $t('Company Information') }}
                </h5>
              </div>
              <div class="card-body">
                <no-data-available height="10" v-if="!isConnected && !loading"/>
                <table v-if="isConnected" class="table table-sm mb-0 receipt-info-table">
                  <tr v-for="(key, index) in Object.keys(companyInfo.main_info)">
                    <td class="font-weight-bold" :class="{'border-top-0': index === 0}">
                      {{ key | removeUnderscores | toUpperCaseFirstSentence }}
                    </td>
                    <td :class="{'border-top-0': index === 0}">{{ companyInfo.main_info[key] }}</td>
                  </tr>
                </table>
              </div>
            </div>
            <div>
              <address-viewer
                  key="company_address"
                  :header-text="$t('Company Address')"
                  :with-actions="false"
                  :hide-icon="true"
                  :address="companyInfo ? companyInfo.company_address : {}"
                  :loader="loading"
              />
            </div>
            <div>
              <address-viewer
                  key="customer_communication_address"
                  :header-text="$t('Customer Communication Address')"
                  :with-actions="false"
                  :hide-icon="true"
                  :address="companyInfo ? companyInfo.customer_communication_address : {}"
                  :loader="loading"
              />
            </div>
            <div>
              <address-viewer
                  key="legal_address"
                  :header-text="$t('Legal Address')"
                  :with-actions="false"
                  :hide-icon="true"
                  :address="companyInfo ? companyInfo.legal_address : {}"
                  :loader="loading"
              />
            </div>
          </div>
        </div>
        <div class="card" v-else-if="!loading">
          <no-data-available :height="10"/>
        </div>
      </div>
    </div>
  </b-tab>
</template>

<script>
  import PageHeader from "@/core/components/page-header/PageHeader";
  import SubmitButton from "@/core/components/common/SubmitButton";
  import AccountingService from "@/modules/setup/accounting/AccountingService";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable";
  import Spinner from "@/core/components/common/Spinner";
  import AddressViewer from "@/modules/customer/view/addresses/AddressViewer";

  export default {
    name: "AccountingTab",
    components: {Spinner, NoDataAvailable, SubmitButton, PageHeader, AddressViewer},
    data() {
      return {
        loading: false,
        companyInfo: null,
        tenants: null,
        selectedTenantId: null,
        savingTenant: false,
        currentTab: 0,
      }
    },
    computed: {
      isConnected() {
        return !!this.companyInfo || !!this.tenants;
      },
    },
    methods: {
      async getInitialData() {
        this.loading = true
        let response = await AccountingService.getAccountingSystemInfo()
        if (response.success) {
          this.companyInfo = response.body.company_info;
        } else if (response.body.non_feild_errors) {
          this.$errorDialog(response.body.non_feild_errors[0]);
        }
        this.loading = false
        return response;
      },
      async onConnectClick() {
        this.loading = true
        let response = await AccountingService.connectToAccountingSystem();
        if (response.success && response.body.url) {
          window.location = response.body.url
        } else if (response.body.non_feild_errors) {
          this.loading = false
          this.$errorDialog(response.body.non_feild_errors[0]);
        }
      },
      async pushData(queryParams) {
        this.loading = true;
        let response = await AccountingService.pushAccountingSystemInfo(queryParams)
        if (response.success) {
          this.$toast('success', this.$i18n.t("Connection with accounting system has been successfully established"));
          if (response.body.tenants) {
            // this is for XeroProvider
            // user has to select tenant
            this.tenants = response.body.tenants
          } else {
            this.companyInfo = response.body.company_info;
          }
        } else if (response.body.non_feild_errors) {
          this.$errorDialog(response.body.non_feild_errors[0]);
        }
        await this.$router.replace({'query': {}});
        this.loading = false;
      },
      async chooseTenant() {
        this.savingTenant = true;
        const response = await AccountingService.saveTenant(this.selectedTenantId)
        if (!response.success) {
          this.$errorDialog(this.$t("Can't save tenant. Please, try again"));
        } else {
          this.companyInfo = response.body.company_info
          this.tenants = null;
          this.selectedTenantId = null;
        }
        this.savingTenant = false;
      }
    },
    async mounted() {
      let queryParams = this.$route.query;
      if (queryParams && Object.keys(queryParams).length > 0) {
        await this.pushData(queryParams);
      } else {
        await this.getInitialData();
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .accounting-tab {

    .content-wrapper {
      min-height: 20em;
    }

    .grid-wrapper {
      display: grid;
      grid-template-columns: 2fr 2fr;
      grid-gap: 1em;
    }
  }
</style>

